/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getU64Decoder,
  getU64Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";

export const RECORD_PAYAI_PAYMENT_DISCRIMINATOR = new Uint8Array([
  165, 202, 159, 139, 65, 65, 36, 163,
]);

export function getRecordPayaiPaymentDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    RECORD_PAYAI_PAYMENT_DISCRIMINATOR,
  );
}

export type RecordPayaiPaymentInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountReputationMetrics extends string | AccountMeta<string> = string,
  TAccountAgent extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TAccountClock extends string | AccountMeta<string> =
    "SysvarC1ock11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountReputationMetrics extends string
        ? WritableAccount<TAccountReputationMetrics>
        : TAccountReputationMetrics,
      TAccountAgent extends string
        ? WritableAccount<TAccountAgent>
        : TAccountAgent,
      TAccountAuthority extends string
        ? ReadonlySignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      TAccountClock extends string
        ? ReadonlyAccount<TAccountClock>
        : TAccountClock,
      ...TRemainingAccounts,
    ]
  >;

export type RecordPayaiPaymentInstructionData = {
  discriminator: ReadonlyUint8Array;
  paymentSignature: string;
  amount: bigint;
  responseTimeMs: bigint;
  success: boolean;
};

export type RecordPayaiPaymentInstructionDataArgs = {
  paymentSignature: string;
  amount: number | bigint;
  responseTimeMs: number | bigint;
  success: boolean;
};

export function getRecordPayaiPaymentInstructionDataEncoder(): Encoder<RecordPayaiPaymentInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      [
        "paymentSignature",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
      ["amount", getU64Encoder()],
      ["responseTimeMs", getU64Encoder()],
      ["success", getBooleanEncoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: RECORD_PAYAI_PAYMENT_DISCRIMINATOR,
    }),
  );
}

export function getRecordPayaiPaymentInstructionDataDecoder(): Decoder<RecordPayaiPaymentInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    [
      "paymentSignature",
      addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder()),
    ],
    ["amount", getU64Decoder()],
    ["responseTimeMs", getU64Decoder()],
    ["success", getBooleanDecoder()],
  ]);
}

export function getRecordPayaiPaymentInstructionDataCodec(): Codec<
  RecordPayaiPaymentInstructionDataArgs,
  RecordPayaiPaymentInstructionData
> {
  return combineCodec(
    getRecordPayaiPaymentInstructionDataEncoder(),
    getRecordPayaiPaymentInstructionDataDecoder(),
  );
}

export type RecordPayaiPaymentAsyncInput<
  TAccountReputationMetrics extends string = string,
  TAccountAgent extends string = string,
  TAccountAuthority extends string = string,
  TAccountClock extends string = string,
> = {
  /** Reputation metrics account */
  reputationMetrics?: Address<TAccountReputationMetrics>;
  /** Agent account */
  agent: Address<TAccountAgent>;
  /** Authority (can be the payment verifier or agent owner) */
  authority: TransactionSigner<TAccountAuthority>;
  /** Clock for timestamps */
  clock?: Address<TAccountClock>;
  paymentSignature: RecordPayaiPaymentInstructionDataArgs["paymentSignature"];
  amount: RecordPayaiPaymentInstructionDataArgs["amount"];
  responseTimeMs: RecordPayaiPaymentInstructionDataArgs["responseTimeMs"];
  success: RecordPayaiPaymentInstructionDataArgs["success"];
};

export async function getRecordPayaiPaymentInstructionAsync<
  TAccountReputationMetrics extends string,
  TAccountAgent extends string,
  TAccountAuthority extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: RecordPayaiPaymentAsyncInput<
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  RecordPayaiPaymentInstruction<
    TProgramAddress,
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountClock
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    reputationMetrics: {
      value: input.reputationMetrics ?? null,
      isWritable: true,
    },
    agent: { value: input.agent ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.reputationMetrics.value) {
    accounts.reputationMetrics.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            114, 101, 112, 117, 116, 97, 116, 105, 111, 110, 95, 109, 101, 116,
            114, 105, 99, 115,
          ]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.agent.value)),
      ],
    });
  }
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.reputationMetrics),
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.clock),
    ],
    data: getRecordPayaiPaymentInstructionDataEncoder().encode(
      args as RecordPayaiPaymentInstructionDataArgs,
    ),
    programAddress,
  } as RecordPayaiPaymentInstruction<
    TProgramAddress,
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountClock
  >);
}

export type RecordPayaiPaymentInput<
  TAccountReputationMetrics extends string = string,
  TAccountAgent extends string = string,
  TAccountAuthority extends string = string,
  TAccountClock extends string = string,
> = {
  /** Reputation metrics account */
  reputationMetrics: Address<TAccountReputationMetrics>;
  /** Agent account */
  agent: Address<TAccountAgent>;
  /** Authority (can be the payment verifier or agent owner) */
  authority: TransactionSigner<TAccountAuthority>;
  /** Clock for timestamps */
  clock?: Address<TAccountClock>;
  paymentSignature: RecordPayaiPaymentInstructionDataArgs["paymentSignature"];
  amount: RecordPayaiPaymentInstructionDataArgs["amount"];
  responseTimeMs: RecordPayaiPaymentInstructionDataArgs["responseTimeMs"];
  success: RecordPayaiPaymentInstructionDataArgs["success"];
};

export function getRecordPayaiPaymentInstruction<
  TAccountReputationMetrics extends string,
  TAccountAgent extends string,
  TAccountAuthority extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: RecordPayaiPaymentInput<
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): RecordPayaiPaymentInstruction<
  TProgramAddress,
  TAccountReputationMetrics,
  TAccountAgent,
  TAccountAuthority,
  TAccountClock
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    reputationMetrics: {
      value: input.reputationMetrics ?? null,
      isWritable: true,
    },
    agent: { value: input.agent ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.reputationMetrics),
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.clock),
    ],
    data: getRecordPayaiPaymentInstructionDataEncoder().encode(
      args as RecordPayaiPaymentInstructionDataArgs,
    ),
    programAddress,
  } as RecordPayaiPaymentInstruction<
    TProgramAddress,
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountClock
  >);
}

export type ParsedRecordPayaiPaymentInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Reputation metrics account */
    reputationMetrics: TAccountMetas[0];
    /** Agent account */
    agent: TAccountMetas[1];
    /** Authority (can be the payment verifier or agent owner) */
    authority: TAccountMetas[2];
    /** Clock for timestamps */
    clock: TAccountMetas[3];
  };
  data: RecordPayaiPaymentInstructionData;
};

export function parseRecordPayaiPaymentInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedRecordPayaiPaymentInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 4) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      reputationMetrics: getNextAccount(),
      agent: getNextAccount(),
      authority: getNextAccount(),
      clock: getNextAccount(),
    },
    data: getRecordPayaiPaymentInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
